import { useState, useMemo } from "react";
import { serializeFilters } from "../serialization/serializeFilters";
import { buildListQueryArgs } from "../query/buildListQueryArgs";
import { usePaginationReset } from "./usePaginationReset";

/**
 * Derive list query args from selected filters + local pagination.
 * Owns serialization and pagination reset (Company `useCompanyData` pattern).
 *
 * @param {{
 *   selectedFilters: Record<string, unknown>,
 *   extras?: Record<string, unknown>,
 *   initialPageSize?: number,
 *   serializeOptions?: { arrayJoin?: string },
 * }} options
 */
export function useFilterQuery({
  selectedFilters,
  extras = {},
  initialPageSize = 10,
  serializeOptions,
} = {}) {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: initialPageSize,
  });

  const queryParams = useMemo(
    () => serializeFilters(selectedFilters, serializeOptions),
    [selectedFilters, serializeOptions]
  );

  usePaginationReset(queryParams, pagination, setPagination);

  const listArgs = useMemo(
    () =>
      buildListQueryArgs({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        filters: queryParams,
        extras,
      }),
    [pagination.pageIndex, pagination.pageSize, queryParams, extras]
  );

  return {
    queryParams,
    listArgs,
    pagination,
    setPagination,
  };
}
